import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { loadDraft, saveDraft, clearDraft } from '@/lib/draftStorage';
import { useCategories } from '@/hooks/useCategories';
import { adjustStock } from '@/hooks/useInventory';
import { useBranch } from '@/context/BranchContext';
import { resolveImageUrl } from '@/lib/imageUrl';
import { friendlyError } from '@/lib/errors';
import { logActivity, logError } from '@/lib/activityLog';
import { processAndUploadProductImage } from '@/lib/imageOptimizer';
import type { Product } from '@/lib/types';

interface ProductFormProps {
  product?: Product | null;
}

interface FormState {
  name: string;
  sku: string;
  barcode: string;
  brand: string;
  description: string;
  category_id: string;
  purchase_price: string;
  sale_price: string;
  wholesale_price: string;
  tax_percentage: string;
  stock: string;
  min_stock: string;
  image_url: string;
  show_in_catalog: boolean;
  active: boolean;
}

const emptyForm: FormState = {
  name: '',
  sku: '',
  barcode: '',
  brand: '',
  description: '',
  category_id: '',
  purchase_price: '',
  sale_price: '',
  wholesale_price: '',
  tax_percentage: '',
  stock: '',
  min_stock: '',
  image_url: '',
  show_in_catalog: true,
  active: true,
};

export default function ProductForm({ product }: ProductFormProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { categories, createCategory } = useCategories();
  const { branches, activeBranchId } = useBranch();
  const [stockBranchId, setStockBranchId] = useState(activeBranchId ?? '');
  const [branchStocks, setBranchStocks] = useState<Record<string, string>>({});
  const [loadingStock, setLoadingStock] = useState(false);
  const draftKey = product
    ? null
    : user?.id
      ? `product:draft:${user.id}`
      : 'product:draft';
  const [form, setForm] = useState<FormState>(() => {
    if (product) {
      return {
        name: product.name,
        sku: product.sku ?? '',
        barcode: product.barcode ?? '',
        brand: product.brand ?? '',
        description: product.description ?? '',
        category_id: product.category_id ?? '',
        purchase_price: product.purchase_price != null ? String(product.purchase_price) : '',
        sale_price: String(product.sale_price ?? ''),
        wholesale_price: product.wholesale_price != null ? String(product.wholesale_price) : '',
        tax_percentage: product.tax_percentage != null ? String(product.tax_percentage) : '',
        stock: String(product.stock ?? 0),
        min_stock: product.min_stock != null ? String(product.min_stock) : '',
        image_url: product.image_url ?? '',
        show_in_catalog: product.show_in_catalog,
        active: product.active,
      };
    }
    const draft = draftKey ? loadDraft<FormState>(draftKey) : null;
    return draft ?? emptyForm;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [showNewCategory, setShowNewCategory] = useState(false);

  const set = (key: keyof FormState, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Guardar el borrador automáticamente para no perder lo que llevas escrito
  useEffect(() => {
    if (draftKey) saveDraft(draftKey, form);
  }, [form, draftKey]);

  useEffect(() => {
    if (!stockBranchId && (activeBranchId || branches[0]?.id)) {
      setStockBranchId(activeBranchId ?? branches[0]?.id ?? '');
    }
  }, [activeBranchId, branches, stockBranchId]);

  // En edición: cargar el stock actual de cada sucursal.
  useEffect(() => {
    if (!product) return;
    let active = true;
    const loadStock = async () => {
      setLoadingStock(true);
      try {
        const { data } = await supabase
          .from('branch_inventory')
          .select('branch_id, stock')
          .eq('product_id', product.id);
        if (!active) return;
        const records = (data ?? []) as { branch_id: string; stock: number }[];
        const map: Record<string, string> = {};
        for (const b of branches) map[b.id] = '0';
        for (const r of records) map[r.branch_id] = String(r.stock ?? 0);
        if (records.length === 0 && (product.stock ?? 0) > 0) {
          const target = activeBranchId ?? branches[0]?.id;
          if (target) map[target] = String(product.stock);
        }
        setBranchStocks(map);
      } finally {
        if (active) setLoadingStock(false);
      }
    };
    loadStock();
    return () => {
      active = false;
    };
  }, [product, branches, activeBranchId]);

  const handleImageUpload = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      // Optimiza (redimensiona + WebP + marca de agua) y publica las variantes.
      const optimized = await processAndUploadProductImage(file);
      set('image_url', optimized.url);
      void logActivity({
        module: 'images',
        action: 'image_uploaded',
        description: `Imagen optimizada y publicada (${optimized.variants.length} tamaños)`,
        targetType: 'image',
        metadata: {
          original_kb: Math.round(optimized.originalBytes / 1024),
          optimized_kb: Math.round(optimized.optimizedBytes / 1024),
          width: optimized.width,
          height: optimized.height,
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo subir la imagen';
      logError('images', 'image_upload_failed', message);
      setError(friendlyError(message, 'No se pudo subir la imagen'));
    } finally {
      setUploading(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      const cat = await createCategory(newCategory);
      if (cat) set('category_id', cat.id);
      setNewCategory('');
      setShowNewCategory(false);
    } catch (err) {
      setError(friendlyError(err, 'No se pudo crear la categoría'));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!form.name.trim()) {
      setError('El nombre del producto es obligatorio.');
      return;
    }
    const salePrice = parseFloat(form.sale_price);
    if (Number.isNaN(salePrice) || salePrice < 0) {
      setError('Ingresa un precio de venta válido.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        sku: form.sku.trim() || null,
        barcode: form.barcode.trim() || null,
        brand: form.brand.trim() || null,
        description: form.description.trim() || null,
        category_id: form.category_id || null,
        purchase_price: form.purchase_price ? parseFloat(form.purchase_price) : null,
        sale_price: salePrice,
        wholesale_price: form.wholesale_price ? parseFloat(form.wholesale_price) : null,
        tax_percentage: form.tax_percentage ? parseFloat(form.tax_percentage) : 0,
        min_stock: form.min_stock ? parseInt(form.min_stock, 10) : 0,
        image_url: form.image_url.trim() || null,
        show_in_catalog: form.show_in_catalog,
        active: form.active,
        updated_at: new Date().toISOString(),
      };

      if (product) {
        // Validar el stock de cada sucursal antes de guardar nada.
        const targets: { branchId: string; name: string; target: number }[] = [];
        for (const b of branches) {
          const raw = branchStocks[b.id];
          const target = raw === '' || raw === undefined ? 0 : parseInt(raw, 10);
          if (Number.isNaN(target) || target < 0) {
            throw new Error(`Stock inválido para la sucursal ${b.name}.`);
          }
          targets.push({ branchId: b.id, name: b.name, target });
        }

        const { error: e } = await supabase
          .from('products')
          .update(payload)
          .eq('id', product.id);
        if (e) throw e;

        // Stock actual por sucursal (para calcular el ajuste real).
        const { data: currentInv } = await supabase
          .from('branch_inventory')
          .select('branch_id, stock')
          .eq('product_id', product.id);
        const currentMap = new Map<string, number>();
        for (const r of (currentInv ?? []) as { branch_id: string; stock: number }[]) {
          currentMap.set(r.branch_id, Number(r.stock ?? 0));
        }

        let totalStock = 0;
        for (const t of targets) {
          totalStock += t.target;
          const current = currentMap.get(t.branchId) ?? 0;
          const delta = t.target - current;
          if (delta !== 0) {
            await adjustStock({
              product_id: product.id,
              quantity: delta,
              type: 'ajuste',
              reason: 'Edición de producto',
              note: 'Stock actualizado desde el formulario del producto',
              branch_id: t.branchId,
            });
          }
        }

        // Mantener el stock global del producto igual a la suma por sucursal.
        const { error: e2 } = await supabase
          .from('products')
          .update({ stock: totalStock })
          .eq('id', product.id);
        if (e2) throw e2;

        void logActivity({
          module: 'products',
          action: 'product_updated',
          description: `Producto actualizado: ${payload.name}`,
          targetType: 'product',
          targetId: product.id,
          metadata: {
            name: payload.name,
            sale_price: salePrice,
            previous_sale_price: product.sale_price,
            price_changed: Number(product.sale_price) !== salePrice,
          },
          branchId: activeBranchId ?? null,
        });
        setSuccess('Producto actualizado correctamente.');
      } else {
        const stockQty = form.stock ? parseInt(form.stock, 10) : 0;
        const { data: createdData, error: e } = await supabase
          .from('products')
          .insert({ ...payload, stock: stockQty })
          .select('id')
          .single();
        if (e) throw e;
        if (stockQty > 0 && stockBranchId) {
          // Upsert: la fila de inventario ya puede existir (la crea un trigger
          // al insertar el producto), así que actualizamos en vez de insertar.
          const { error: invErr } = await supabase
            .from('branch_inventory')
            .upsert(
              { branch_id: stockBranchId, product_id: (createdData as { id: string }).id, stock: stockQty },
              { onConflict: 'branch_id,product_id' }
            );
          if (invErr) throw invErr;
        }
        void logActivity({
          module: 'products',
          action: 'product_created',
          description: `Producto creado: ${payload.name}`,
          targetType: 'product',
          targetId: (createdData as { id: string }).id,
          metadata: { name: payload.name, sale_price: salePrice, sku: payload.sku },
          branchId: activeBranchId ?? null,
        });
        if (draftKey) clearDraft(draftKey);
        setSuccess('Producto creado correctamente.');
        navigate('/products');
      }
    } catch (err) {
      setError(friendlyError(err, 'No se pudo guardar el producto'));
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 placeholder:text-foreground-400';
  const labelClass = 'block text-sm font-medium text-foreground-800 mb-1.5';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 flex items-center gap-3">
          <i className="ri-error-warning-line text-accent-700" />
          <p className="text-sm text-foreground-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 flex items-center gap-3">
          <i className="ri-checkbox-circle-line text-accent-700" />
          <p className="text-sm text-foreground-700">{success}</p>
        </div>
      )}

      {/* Información básica */}
      <section className="rounded-lg border border-background-200 bg-background-50 p-5">
        <h3 className="font-heading font-semibold text-foreground-950">Información básica</h3>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className={labelClass}>Nombre *</label>
            <input
              className={inputClass}
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="Ej. Coca Cola 355ml"
            />
          </div>

          <div>
            <label className={labelClass}>Foto</label>
            <div className="flex items-center gap-3">
              {form.image_url ? (
                <div className="w-16 h-16 rounded-md overflow-hidden border border-background-200 bg-background-100 shrink-0">
                  <img src={resolveImageUrl(form.image_url) || ''} alt="Producto" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-16 h-16 rounded-md border border-dashed border-background-300 bg-background-100 flex items-center justify-center text-foreground-400 shrink-0">
                  <i className="ri-image-line text-xl" />
                </div>
              )}
              <div className="flex-1 space-y-2">
                <label className="inline-flex items-center gap-2 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 transition cursor-pointer">
                  <i className="ri-upload-line" />
                  {uploading ? 'Optimizando...' : 'Subir imagen'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e.target.files?.[0])}
                  />
                </label>
                <p className="text-xs text-foreground-500">
                  La imagen se optimiza (WebP) y lleva marca de agua DK SUPPLY automáticamente.
                </p>
                <input
                  className={inputClass}
                  value={form.image_url}
                  onChange={(e) => set('image_url', e.target.value)}
                  placeholder="o pega una URL de imagen"
                />
              </div>
            </div>
          </div>

          <div>
            <label className={labelClass}>Marca</label>
            <input
              className={inputClass}
              value={form.brand}
              onChange={(e) => set('brand', e.target.value)}
              placeholder="Ej. Coca-Cola"
            />
          </div>

          <div>
            <label className={labelClass}>Categoría</label>
            <div className="flex gap-2">
              <select
                className={inputClass}
                value={form.category_id}
                onChange={(e) => set('category_id', e.target.value)}
              >
                <option value="">Sin categoría</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setShowNewCategory((v) => !v)}
                className="shrink-0 w-10 h-10 rounded-md border border-background-300 flex items-center justify-center text-foreground-700 hover:bg-background-100 cursor-pointer"
                title="Nueva categoría"
              >
                <i className="ri-add-line" />
              </button>
            </div>
            {showNewCategory && (
              <div className="mt-2 flex gap-2">
                <input
                  className={inputClass}
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Nombre de la categoría"
                />
                <button
                  type="button"
                  onClick={handleAddCategory}
                  className="shrink-0 rounded-md bg-primary-500 hover:bg-primary-600 px-3 py-2 text-sm font-semibold text-background-50 whitespace-nowrap cursor-pointer"
                >
                  Agregar
                </button>
              </div>
            )}
          </div>

          <div className="md:col-span-2">
            <label className={labelClass}>Descripción</label>
            <textarea
              className={`${inputClass} resize-none`}
              rows={3}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Descripción corta del producto"
            />
          </div>
        </div>
      </section>

      {/* Identificación */}
      <section className="rounded-lg border border-background-200 bg-background-50 p-5">
        <h3 className="font-heading font-semibold text-foreground-950">Identificación</h3>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>SKU</label>
            <input
              className={inputClass}
              value={form.sku}
              onChange={(e) => set('sku', e.target.value)}
              placeholder="Ej. CC-001"
            />
          </div>
          <div>
            <label className={labelClass}>Código de barra</label>
            <input
              className={inputClass}
              value={form.barcode}
              onChange={(e) => set('barcode', e.target.value)}
              placeholder="Ej. 7501055300846"
            />
          </div>
        </div>
      </section>

      {/* Precios */}
      <section className="rounded-lg border border-background-200 bg-background-50 p-5">
        <h3 className="font-heading font-semibold text-foreground-950">Precios</h3>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Precio de compra</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className={inputClass}
              value={form.purchase_price}
              onChange={(e) => set('purchase_price', e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div>
            <label className={labelClass}>Precio de venta *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className={inputClass}
              value={form.sale_price}
              onChange={(e) => set('sale_price', e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div>
            <label className={labelClass}>Precio mayorista</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className={inputClass}
              value={form.wholesale_price}
              onChange={(e) => set('wholesale_price', e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div>
            <label className={labelClass}>Impuesto (%)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              className={inputClass}
              value={form.tax_percentage}
              onChange={(e) => set('tax_percentage', e.target.value)}
              placeholder="0"
            />
          </div>
        </div>
      </section>

      {/* Inventario */}
      <section className="rounded-lg border border-background-200 bg-background-50 p-5">
        <h3 className="font-heading font-semibold text-foreground-950">Inventario</h3>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {!product && (
            <div>
              <label className={labelClass}>Stock inicial</label>
              <input
                type="number"
                min="0"
                className={inputClass}
                value={form.stock}
                onChange={(e) => set('stock', e.target.value)}
                placeholder="0"
              />
            </div>
          )}
          <div>
            <label className={labelClass}>Stock mínimo</label>
            <input
              type="number"
              min="0"
              className={inputClass}
              value={form.min_stock}
              onChange={(e) => set('min_stock', e.target.value)}
              placeholder="0"
            />
          </div>
          {!product && (
            <div className="md:col-span-2">
              <label className={labelClass}>Sucursal de stock inicial</label>
              <select
                value={stockBranchId}
                onChange={(e) => setStockBranchId(e.target.value)}
                className={inputClass}
              >
                <option value="">Selecciona una sucursal</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {product && (
          <div className="mt-5 border-t border-background-200 pt-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-foreground-800">Stock actual por sucursal</p>
              <p className="text-xs text-foreground-600">
                Total:{' '}
                <span className="font-semibold text-foreground-950">
                  {Object.values(branchStocks).reduce((s, v) => s + (parseInt(v, 10) || 0), 0)}
                </span>
              </p>
            </div>
            {branches.length === 0 ? (
              <p className="mt-2 text-sm text-foreground-500">No hay sucursales configuradas.</p>
            ) : loadingStock ? (
              <div className="mt-3 flex items-center gap-2 text-sm text-foreground-500">
                <span className="w-4 h-4 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
                Cargando stock...
              </div>
            ) : (
              <div className="mt-3 space-y-2">
                {branches.map((b) => (
                  <div
                    key={b.id}
                    className="flex items-center justify-between gap-3 rounded-md border border-background-200 px-3 py-2.5"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <i className="ri-building-2-line text-foreground-500" />
                      <span className="text-sm font-medium text-foreground-900 truncate">{b.name}</span>
                      {!b.active && <span className="text-xs text-foreground-500">(inactiva)</span>}
                    </div>
                    <input
                      type="number"
                      min="0"
                      value={branchStocks[b.id] ?? '0'}
                      onChange={(e) =>
                        setBranchStocks((prev) => ({ ...prev, [b.id]: e.target.value }))
                      }
                      className="w-28 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-right text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                  </div>
                ))}
              </div>
            )}
            <p className="mt-2 text-xs text-foreground-500">
              Al guardar, cada cambio queda registrado como un ajuste de inventario en esa sucursal.
            </p>
          </div>
        )}
      </section>

      {/* Catálogo */}
      <section className="rounded-lg border border-background-200 bg-background-50 p-5">
        <h3 className="font-heading font-semibold text-foreground-950">Catálogo y estado</h3>
        <div className="mt-4 space-y-3">
          <label className="flex items-center justify-between gap-3 rounded-md border border-background-200 p-3 cursor-pointer">
            <div>
              <p className="text-sm font-medium text-foreground-900">Mostrar en catálogo</p>
              <p className="text-xs text-foreground-600">Visible públicamente en /catalogo</p>
            </div>
            <input
              type="checkbox"
              checked={form.show_in_catalog}
              onChange={(e) => set('show_in_catalog', e.target.checked)}
              className="w-5 h-5 accent-primary-500 cursor-pointer"
            />
          </label>
          <label className="flex items-center justify-between gap-3 rounded-md border border-background-200 p-3 cursor-pointer">
            <div>
              <p className="text-sm font-medium text-foreground-900">Producto activo</p>
              <p className="text-xs text-foreground-600">Desactívalo para ocultarlo del POS y del catálogo</p>
            </div>
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => set('active', e.target.checked)}
              className="w-5 h-5 accent-primary-500 cursor-pointer"
            />
          </label>
        </div>
      </section>

      <div className="flex items-center gap-3 pt-2">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
        >
          <i className="ri-save-line" />
          {saving ? 'Guardando...' : 'Guardar producto'}
        </button>
        <button
          type="button"
          onClick={() => {
            if (draftKey) clearDraft(draftKey);
            navigate('/products');
          }}
          className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}